import React, { useState, useMemo } from 'react';
import { Element } from 'react-scroll';
import {
  CaretDownIcon,
  FolderIcon,
  TrashIcon,
  PlusIcon,
  CircleNotchIcon,
  PencilLineIcon,
  FloppyDiskIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { Button } from 'src/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import GentlyTable from 'src/components/common/GentlyTable';
import {
  ProductGroup,
  useUpdateProductGroup,
} from 'src/api/rebates/useProductGroups';
import { RebateRule, REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import { useProductGroupTagMutations } from 'src/api/rebates/useProductGroupTagMutations';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { TagsDisplay } from 'src/components/rebates/TagsDisplay';
import { ProductGroupDeletionDialog } from 'src/components/rebates/ProductGroupDeletionDialog';

interface ProductGroupsSectionProps {
  productGroupsRef: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onToggle: (open: boolean) => void;
  allProductGroups: ProductGroup[] | undefined;
  productGroupsLoading: boolean;
  rebateRules: RebateRule[] | undefined;
  onCreateProductGroup: () => void;
  onNavigateToSection: (sectionId: string) => void;
  refetchProductGroups: () => void;
}

export function ProductGroupsSection({
  productGroupsRef,
  isExpanded,
  onToggle,
  allProductGroups,
  productGroupsLoading,
  rebateRules,
  onCreateProductGroup,
  onNavigateToSection,
  refetchProductGroups,
}: ProductGroupsSectionProps) {
  // State for inline editing
  const [editingProductGroup, setEditingProductGroup] = useState<string | null>(
    null,
  );
  const [editFormData, setEditFormData] = useState<{
    name: string;
    description: string;
    isHistorical: boolean;
  }>({ name: '', description: '', isHistorical: false });

  // State for deletion flow
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [selectedProductGroupForDeletion, setSelectedProductGroupForDeletion] =
    useState<ProductGroup | null>(null);

  // Product group mutation hooks
  const updateProductGroupMutation = useUpdateProductGroup(
    editingProductGroup || '',
  );

  // Tag mutation hook
  const {
    handleProductGroupTagAdd,
    handleProductGroupTagRemove,
    tagsUpdatingByProductGroupId,
  } = useProductGroupTagMutations();

  // Fetch available Product Family Tags
  const { data: productFamilyTagsEnum, isLoading: tagsEnumLoading } =
    useCoreEnums({
      name: REBATES_SYSTEM_ENUMS.PRODUCT_FAMILY_TAGS.name,
      includeOptions: true,
    });

  // Get available tags from the enum
  const availableTags = useMemo(() => {
    const edges = productFamilyTagsEnum?.edges;
    if (!edges || edges.length === 0) {
      return [];
    }
    return edges[0].options || [];
  }, [productFamilyTagsEnum]);

  // Calculate product group usage in rebate rules
  const calculateProductGroupUsage = (productGroupId: string) => {
    if (!rebateRules) return { usedInRules: [], totalUsageCount: 0 };

    const usedInRules: Array<{
      id: string;
      name: string;
      type: 'qualifying' | 'affected' | 'both';
    }> = [];

    rebateRules.forEach((rule) => {
      // Check if product group is explicitly listed
      const isInQualifyingList = rule.qualifyingProductGroups?.some(
        (pg) => pg.id === productGroupId,
      );
      const isInAffectedList = rule.affectedProductGroups?.some(
        (pg) => pg.id === productGroupId,
      );

      // Determine actual usage based on inclusion type
      // For inclusive rules: listed = included, not listed = excluded
      // For exclusive rules: listed = excluded, not listed = included
      const isQualifyingUsed =
        rule.qualifyingInclusionType === 'exclusive'
          ? !isInQualifyingList // If exclusive, NOT being in the list means it's used
          : isInQualifyingList; // If inclusive, being in the list means it's used

      const isAffectedUsed =
        rule.affectedInclusionType === 'exclusive'
          ? !isInAffectedList // If exclusive, NOT being in the list means it's used
          : isInAffectedList; // If inclusive, being in the list means it's used

      if (isQualifyingUsed && isAffectedUsed) {
        usedInRules.push({ id: rule.id, name: rule.name, type: 'both' });
      } else if (isQualifyingUsed) {
        usedInRules.push({ id: rule.id, name: rule.name, type: 'qualifying' });
      } else if (isAffectedUsed) {
        usedInRules.push({ id: rule.id, name: rule.name, type: 'affected' });
      }
    });

    return { usedInRules, totalUsageCount: usedInRules.length };
  };

  // Inline editing handlers
  const handleStartEdit = (productGroup: ProductGroup) => {
    setEditingProductGroup(productGroup.id);
    setEditFormData({
      name: productGroup.name,
      description: productGroup.description || '',
      isHistorical: productGroup.isHistorical || false,
    });
  };

  const handleCancelEdit = () => {
    setEditingProductGroup(null);
    setEditFormData({ name: '', description: '', isHistorical: false });
  };

  const handleSaveEdit = async () => {
    if (!editingProductGroup) return;

    try {
      await updateProductGroupMutation.mutateAsync({
        name: editFormData.name,
        description: editFormData.description,
        isHistorical: editFormData.isHistorical,
      });

      refetchProductGroups();
      setEditingProductGroup(null);
      setEditFormData({ name: '', description: '', isHistorical: false });
    } catch (error) {
      console.error('Error updating product group:', error);
    }
  };

  // Handle clicking the delete button - show dialog
  const handleDeleteProductGroup = (productGroup: ProductGroup) => {
    // Prevent deletion of catch-all groups
    if (productGroup.isCatchAll) {
      return;
    }

    setSelectedProductGroupForDeletion(productGroup);
    setDeletionDialogOpen(true);
  };

  // Close the deletion dialog
  const handleCloseDeletionDialog = () => {
    setDeletionDialogOpen(false);
    setSelectedProductGroupForDeletion(null);
  };

  // Use only real product groups (catch-all groups are now real groups)
  const displayProductGroups: ProductGroup[] = allProductGroups || [];

  // Calculate total count
  const totalGroupsCount = displayProductGroups.length;

  // Check if any product groups have enableProductCodes in metadata
  const hasEnableProductCodes = displayProductGroups.some(
    (pg) =>
      pg.metadata?.enableProductCodes &&
      pg.metadata.enableProductCodes.length > 0,
  );

  return (
    <>
      <Element name="productGroups">
        <div ref={productGroupsRef}>
          <Card>
            <Collapsible open={isExpanded} onOpenChange={onToggle}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FolderIcon className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-semibold">
                        Product Groups ({totalGroupsCount})
                      </h2>
                    </div>
                    <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {productGroupsLoading ? (
                    <div className="flex justify-center p-6">
                      <CircleNotchIcon className="h-6 w-6 animate-spin" />
                    </div>
                  ) : (
                    <GentlyTable
                      showDownload={false}
                      data={displayProductGroups}
                      getRowClassName={(row) =>
                        row.isCatchAll ? 'bg-blue-50/50' : ''
                      }
                      columns={[
                        {
                          id: 'name',
                          label: 'Name',
                          sortable: true,
                          render: (value: string, row: ProductGroup) => {
                            const usage = calculateProductGroupUsage(row.id);
                            const isUnused = usage.totalUsageCount === 0;
                            const isEditing = editingProductGroup === row.id;

                            if (isEditing) {
                              return (
                                <Input
                                  value={editFormData.name}
                                  onChange={(e) =>
                                    setEditFormData((prev) => ({
                                      ...prev,
                                      name: e.target.value,
                                    }))
                                  }
                                  className="h-8 text-sm"
                                  placeholder="Product group name"
                                />
                              );
                            }

                            return (
                              <div className="flex items-center gap-2">
                                <span
                                  className={cn(
                                    'font-medium',
                                    isUnused && 'text-muted-foreground',
                                  )}
                                >
                                  {value}
                                </span>
                                {row.isCatchAll && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs shrink-0"
                                  >
                                    Default
                                  </Badge>
                                )}
                                {usage.totalUsageCount > 0 && (
                                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                    {usage.totalUsageCount}
                                  </span>
                                )}
                              </div>
                            );
                          },
                        },
                        {
                          id: 'description',
                          label: 'Description',
                          sortable: true,
                          render: (value: string, row: ProductGroup) => {
                            const usage = calculateProductGroupUsage(row.id);
                            const isUnused = usage.totalUsageCount === 0;
                            const isEditing = editingProductGroup === row.id;

                            if (isEditing) {
                              return (
                                <Input
                                  value={editFormData.description}
                                  onChange={(e) =>
                                    setEditFormData((prev) => ({
                                      ...prev,
                                      description: e.target.value,
                                    }))
                                  }
                                  className="h-8 text-sm"
                                  placeholder="Description (optional)"
                                />
                              );
                            }

                            return (
                              <span
                                className={cn(
                                  isUnused && 'text-muted-foreground',
                                )}
                              >
                                {value || '-'}
                              </span>
                            );
                          },
                        },
                        ...(hasEnableProductCodes
                          ? [
                              {
                                id: 'internalCodes' as keyof ProductGroup,
                                label: 'Enable Codes',
                                sortable: false,
                                render: (
                                  _value: unknown,
                                  row: ProductGroup,
                                ) => {
                                  const codes =
                                    row.metadata?.enableProductCodes;
                                  if (!codes || codes.length === 0) {
                                    return (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    );
                                  }
                                  return (
                                    <div className="flex flex-wrap gap-1">
                                      {codes.map((code) => (
                                        <Badge
                                          key={code}
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {code}
                                        </Badge>
                                      ))}
                                    </div>
                                  );
                                },
                              },
                            ]
                          : []),
                        {
                          id: 'isHistorical' as keyof ProductGroup,
                          label: 'Historical',
                          sortable: true,
                          render: (_value: unknown, row: ProductGroup) => {
                            // Catch-all groups cannot be historical
                            if (row.isCatchAll) {
                              return null;
                            }

                            return row.isHistorical ? (
                              <Badge
                                variant="secondary"
                                className="bg-gray-100 text-gray-800"
                              >
                                Historical
                              </Badge>
                            ) : null;
                          },
                        },
                        {
                          id: 'tags' as keyof ProductGroup,
                          label: 'Product Family Tags',
                          sortable: false,
                          render: (_value: unknown, row: ProductGroup) => {
                            // Convert ProductGroup tags to TagAssociation format expected by TagsDisplay
                            const currentTags = (row.tags || []).map((tag) => ({
                              id: tag.id,
                              coreEnumOptionId: tag.id,
                              coreEnumOption: {
                                id: tag.id,
                                name: tag.name,
                                description: tag.description,
                                sortOrder: 0,
                                enumId: '',
                              },
                            }));
                            const currentTagIdArray = currentTags.map(
                              (t) => t.coreEnumOptionId,
                            );
                            const currentTagIds = new Set(currentTagIdArray);
                            const isUpdating =
                              tagsUpdatingByProductGroupId.get(row.id) ?? false;

                            return (
                              <div onClick={(e) => e.stopPropagation()}>
                                <TagsDisplay
                                  currentTags={currentTags}
                                  availableTags={availableTags}
                                  currentTagIds={currentTagIds}
                                  isLoading={tagsEnumLoading}
                                  isUpdating={isUpdating}
                                  maxTags={1}
                                  onAddTag={(tagId) =>
                                    handleProductGroupTagAdd(
                                      row.id,
                                      tagId,
                                      currentTagIdArray,
                                    )
                                  }
                                  onRemoveTag={(tagId) =>
                                    handleProductGroupTagRemove(
                                      row.id,
                                      tagId,
                                      currentTagIdArray,
                                    )
                                  }
                                  layout="compact"
                                  showEmptyState={false}
                                />
                              </div>
                            );
                          },
                        },
                        {
                          id: 'usedInRules' as keyof ProductGroup,
                          label: 'Used in Rules',
                          sortable: false,
                          render: (_value: unknown, row: ProductGroup) => {
                            const usage = calculateProductGroupUsage(row.id);

                            if (usage.totalUsageCount === 0) {
                              return (
                                <span className="text-sm text-muted-foreground italic">
                                  Not used in any rules
                                </span>
                              );
                            }

                            return (
                              <div className="flex flex-wrap gap-1">
                                {usage.usedInRules.map((ruleUsage) => (
                                  <div
                                    key={ruleUsage.id}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:opacity-80 transition-opacity bg-blue-100 text-blue-800"
                                    onClick={() =>
                                      onNavigateToSection(
                                        `rule-${ruleUsage.id}`,
                                      )
                                    }
                                    title={`Used in rebate rule: ${ruleUsage.name}`}
                                  >
                                    {ruleUsage.name}
                                  </div>
                                ))}
                              </div>
                            );
                          },
                        },
                        {
                          id: 'actions' as keyof ProductGroup,
                          label: 'Actions',
                          sortable: false,
                          align: 'center' as const,
                          render: (_value: unknown, row: ProductGroup) => {
                            const isEditing = editingProductGroup === row.id;

                            if (isEditing) {
                              return (
                                <div className="flex gap-1 justify-center">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                          onClick={handleSaveEdit}
                                          disabled={
                                            updateProductGroupMutation.isPending
                                          }
                                        >
                                          {updateProductGroupMutation.isPending ? (
                                            <CircleNotchIcon className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <FloppyDiskIcon className="h-4 w-4" />
                                          )}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Save Changes</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                          onClick={handleCancelEdit}
                                          disabled={
                                            updateProductGroupMutation.isPending
                                          }
                                        >
                                          <XIcon className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Cancel</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              );
                            }

                            return (
                              <div className="flex gap-1 justify-center">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        onClick={() => handleStartEdit(row)}
                                        disabled={editingProductGroup !== null}
                                      >
                                        <PencilLineIcon className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Edit Product Group</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {!row.isCatchAll && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                          onClick={() =>
                                            handleDeleteProductGroup(row)
                                          }
                                          disabled={
                                            editingProductGroup !== null
                                          }
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Delete Product Group</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            );
                          },
                        },
                      ]}
                      searchFields={['name', 'description']}
                      searchPlaceholder="Search product groups..."
                      emptyMessage="No product groups found for this supplier"
                      actions={
                        <Button
                          onClick={onCreateProductGroup}
                          variant="outline"
                          size="sm"
                          className="gap-2"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Add Product Group
                        </Button>
                      }
                    />
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>
      </Element>

      {/* Product Group Deletion Dialog */}
      <ProductGroupDeletionDialog
        open={deletionDialogOpen}
        onClose={handleCloseDeletionDialog}
        productGroup={selectedProductGroupForDeletion}
        onDeleted={refetchProductGroups}
      />
    </>
  );
}
